import * as React from "react";
import {
  DatePicker,
  IDatePicker,
  IDatePickerStrings,
  IDatePickerStyles,
  IStackTokens,
  ITextFieldStyles,
  mergeStyleSets,
  Stack,
  TextField,
} from "@fluentui/react";
import * as Messages from "../../codegen/Messages";
import { formatLocalizedTime } from "../../helpers/dateTimeUtils";
import { buildTestId } from "../../helpers/testIdHelper";
import { uniqueGUID } from "../../helpers/util";
import { BaseInputProps } from "./BaseInput";
import {
  FormContext,
  FormErrorsContext,
  FormInputGroupLayout,
  FormInputGroupLayoutContext,
  FormState,
  FormValidationModeContext,
  getFieldErrors,
  ValidationState,
} from "./Form";
import { buildLabelWrapperTestIds, LabelWrapper, LabelWrapperTestIds } from "./LabelWrapper";

export interface DateTimePickerProps extends BaseInputProps {
  /**
   * Label for the Date and time picker Field
   */
  label?: string;
  /**
  * Locale for the Date and time picker Field
  */
  locale: string;
  /**
   * Default value for the date field
   */
  defaultValue?: Date;
  /**
  * Callback to validate the date and time input
  */
  validator?: (value: Date) => string[] | undefined;

  /**
   * The minimum allowable date.
   */
  minDate?: Date;
}

const set24HrsTimeFormat = (str: string): Date => {
  str = String(str).toLowerCase().replace(/\s/g, "");
  const hasAM = str.indexOf("am") >= 0;
  const hasPM = str.indexOf("pm") >= 0;
  // first strip off the am/pm, leave it either hour or hour:minute
  str = str.replace("am", "").replace("pm", "");
  // if hour, convert to hour:00
  if (str.indexOf(":") < 0) str += ":00";
  // now it's hour:minute
  // we add am/pm back if striped out before
  if (hasAM) str += " am";
  if (hasPM) str += " pm";
  // now its either hour:minute, or hour:minute am/pm
  // put it in a date object, it will convert to 24 hours format for us
  const d = new Date(`1/1/2011 ${str}`);
  return d;
};

const enum DateFormatOptions {
  Long = "long",
  Short = "short",
  Narrow = "narrow",
}

const generateDefaultTime = (): Date => {
  const date = new Date();
  date.setHours(24, 0, 0, 0);
  return date;
};

const getMonthNames = (locale: string, format: DateFormatOptions): string[] => {
  const formatter = new Intl.DateTimeFormat(locale, { month: format, timeZone: "UTC" });
  const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => {
    const mm = month < 10 ? `0${month}` : `${month}`;
    return new Date(`2017-${mm}-01T00:00:00+00:00`);
  });
  return months.map(date => formatter.format(date));
};
const getDayNames = (locale: string, format: DateFormatOptions): string[] => {
  const curr = new Date();
  curr.setHours(24, 0, 0, 0);
  const days:Date[] = [];
  for (let i = 0; i < 7; i++) {
    const first = curr.getDate() - curr.getDay() + i;
    const day = new Date(curr.setDate(first));
    days.push(day);
  }
  return days.map(day => day.toLocaleDateString(locale, { weekday: format }));
};

const textFieldStyles: Partial<ITextFieldStyles> = {
  root: {
    width: "100%",
    height: "32px",
    marginTop: "5px",
  },
};
const calendarStyles: Partial<IDatePickerStyles> = {

  root: {
    width: "100%",
    height: "24px",
    marginTop: "5px",
  },
  wrapper: { height: "24px" },
  // textField: { height: "24px !important", fieldGroup: { height: "24px !important" } },
  readOnlyTextField: { maxHeight: "24px !important" },
  // callout: { minWidth: "218px", height: "260px", left: "500px" },

};
const calenderWideStyles = mergeStyleSets(calendarStyles, { root: { minWidth: "218px", height: "24px" } });
const textFieldWideStyles = mergeStyleSets(textFieldStyles, { root: { minWidth: "218px" } });
const stackTokens: Partial<IStackTokens> = { childrenGap: 5 };
const timeFormatOptions: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit", second: "2-digit" };

/**
 * This does not extend ComponentTestIds as there is not one 'core' component but multiple
 */
export type DateTimePickerTestIds = { date: string; time: string; } & LabelWrapperTestIds;

export const buildDateTimePickerTestIds = (testId?: string): DateTimePickerTestIds => ({
  ...buildLabelWrapperTestIds(testId),
  date: buildTestId(testId, "-date"),
  time: buildTestId(testId, "-time"),
});
/**
 * The Date and Time picker component provides an option to  select a date and time
 */
export const DateTimePicker = ({
  testId,
  fieldName,
  groupName,
  label,
  locale,
  disabled,
  defaultValue,
  required,
  placeholder,
  tooltip,
  statusInfo,
  subField,
  validator,
  minDate,
}: DateTimePickerProps): JSX.Element => {
  const { groupName: layoutGroup, layout } = React.useContext(
    FormInputGroupLayoutContext,
  );

  const group = groupName || layoutGroup;
  const form: FormState = React.useContext(FormContext);
  if (!Object.keys(form).length) {
    throw new Error("Date and time picker should be used within form");
  }

  const datePickerRef = React.useRef<IDatePicker>(null);
  const [dateValue, setDateValue] = React.useState<Date>(defaultValue || new Date());
  const [selectedTime, setSelectedTime] = React.useState<string>();
  const [timeValue, setTimeValue] = React.useState<Date>(generateDefaultTime());
  const validationMode = React.useContext(FormValidationModeContext);
  const formErrors = React.useContext(FormErrorsContext);
  const fieldErrors = getFieldErrors(formErrors, fieldName, group);
  const [errorMessage, setErrorMessage] = React.useState<string[]>();
  const [timeFormatMessage, setTimeFormatMessage] = React.useState<string[]>();
  const [key, setKey] = React.useState<string>(uniqueGUID());

  const localizedDatePickerStrings: IDatePickerStrings = {
    days: getDayNames(locale, DateFormatOptions.Long),
    goToToday: "",
    months: getMonthNames(locale, DateFormatOptions.Long),
    shortDays: getDayNames(locale, DateFormatOptions.Narrow),
    shortMonths: getMonthNames(locale, DateFormatOptions.Short),
  };

  const onFormatDate = (date? : Date): string => (
    !date ? "" : date.toLocaleDateString(locale)
  );

  const allErrors = [...errorMessage || [], ...fieldErrors || []];

  const dateTimePickerTestIds = buildDateTimePickerTestIds(testId);

  React.useEffect(() => {
    form.registerFieldLabel(fieldName, label);
    form.registerFieldGroup(fieldName, group);

    // always register the field since the time picker has an internal time validation
    form.registerField(fieldName, group);
    return function cleanup() {
      form.unRegisterField(fieldName, group);
    };
    // Only at mount time. Others props should not have side effect beyond their initial values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fieldValidation = () : void => {
    const errorMsg = validator ? validator(form.getValue(fieldName, group)) : undefined;
    const invalidTime = timeFormatMessage || undefined;
    const errors = [...invalidTime || [], ...errorMsg || []];
    form.setFieldState(
      errors && errors?.length > 0 ? ValidationState.INVALID : ValidationState.VALID,
      fieldName,
      group,
    );
    setErrorMessage(errors);
  };

  React.useEffect(() => {
    form.setValue(new Date(
      dateValue.getFullYear(),
      dateValue.getMonth(),
      dateValue.getDate(),
      timeValue.getHours(),
      timeValue.getMinutes(),
      timeValue.getSeconds(),
    ).getTime(), fieldName, group);

    fieldValidation();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateValue, timeValue]);

  const internalDateOnChange = (
    date: Date | null | undefined,
  ): void => {
    if (date !== undefined && date !== null) {
      setDateValue(date);
    }
  };

  const internalTimeOnChange = (
    _: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string,
  ): void => {
    if (newValue !== undefined) {
      if (newValue !== "") {
        setSelectedTime(newValue);
      } else {
        setSelectedTime(`${generateDefaultTime().getHours()}`);
      }
    }
  };

  const validateSelectedTime = (): void => {
    if (selectedTime !== undefined) {
      if (selectedTime !== "" && Number.isNaN(set24HrsTimeFormat(selectedTime).getHours())) {
        setTimeFormatMessage(
          Number.isNaN(set24HrsTimeFormat(selectedTime).getHours()) ? [Messages.validation.timePickerValidation()] : [],
        );
        setTimeValue(
          Number.isNaN(set24HrsTimeFormat(selectedTime).getHours()) ? generateDefaultTime()
            : set24HrsTimeFormat(selectedTime),
        );
      } else if (!Number.isNaN(set24HrsTimeFormat(selectedTime).getHours())) {
        setTimeFormatMessage(undefined);
        setTimeValue(set24HrsTimeFormat(selectedTime));
        setKey(uniqueGUID);
      }
    }
  };

  React.useEffect(() => {
    fieldValidation();
    // Only if validation mode changes. Others props should not have side effect beyond their initial values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationMode]);

  return (
    <LabelWrapper
      errors={allErrors}
      fieldName={fieldName}
      layout={layout}
      label={label}
      required={required}
      statusInfo={statusInfo}
      subField={subField}
      tooltip={tooltip}
      testId={testId}
    >
      <Stack horizontal tokens={stackTokens}>
        <DatePicker
          data-test-id={dateTimePickerTestIds.date}
          componentRef={datePickerRef}
          disabled={disabled}
          placeholder={placeholder}
          value={new Date(dateValue)}
          formatDate={onFormatDate}
          onSelectDate={internalDateOnChange}
          isMonthPickerVisible={false}
          showGoToToday={false}
          strings={localizedDatePickerStrings}
          minDate={minDate}
          styles={
          layout === FormInputGroupLayout.COMPACT
            ? calendarStyles
            : calenderWideStyles
        }
        />
        <TextField
          data-test-id={dateTimePickerTestIds.time}
          onBlur={validateSelectedTime}
          key={key}
          disabled={disabled}
          placeholder={placeholder}
          defaultValue={formatLocalizedTime(locale, timeValue, timeFormatOptions)}
          validateOnFocusOut
          onChange={internalTimeOnChange}
          styles={
          layout === FormInputGroupLayout.COMPACT
            ? textFieldStyles
            : textFieldWideStyles
        }
        />
      </Stack>
    </LabelWrapper>
  );
};
